/**
 * <review-carousel> — a horizontal, one-card-at-a-time carousel of text reviews.
 * Dots pagination (built from the number of slides), optional arrows, and optional
 * autoplay on a configurable timer. Autoplay pauses on hover / focus / touch and
 * when the tab is hidden, and is disabled when the user prefers reduced motion.
 * Vanilla custom element — no Horizon ESM dependencies.
 */
if (!customElements.get('review-carousel')) {
  customElements.define(
    'review-carousel',
    class ReviewCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-rc-track]');
        if (!this.track) return;

        this.slides = Array.from(this.track.children);
        this.dotsWrap = this.querySelector('[data-rc-dots]');
        this.prevBtn = this.querySelector('[data-rc-prev]');
        this.nextBtn = this.querySelector('[data-rc-next]');
        this.autoplay = this.dataset.autoplay === 'true';
        this.interval = Math.max(1500, parseInt(this.dataset.interval, 10) || 5000);
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.index = 0;

        // Nothing to paginate with a single review.
        if (this.slides.length <= 1) {
          this.dotsWrap?.remove();
          this.prevBtn?.remove();
          this.nextBtn?.remove();
          return;
        }

        this.buildDots();
        this.bindNav();
        this.observe();

        if (this.autoplay && !this.reduceMotion) this.start();
      }

      disconnectedCallback() {
        this.stop();
        this.io?.disconnect();
        if (this.onVis) document.removeEventListener('visibilitychange', this.onVis);
      }

      buildDots() {
        if (!this.dotsWrap) return;
        this.dots = this.slides.map((_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'rc__dot';
          dot.setAttribute('aria-label', `Go to review ${i + 1}`);
          dot.addEventListener('click', () => {
            this.goTo(i);
            this.restart();
          });
          this.dotsWrap.appendChild(dot);
          return dot;
        });
        this.setActive(0);
      }

      bindNav() {
        this.prevBtn?.addEventListener('click', () => {
          this.goTo(this.index - 1);
          this.restart();
        });
        this.nextBtn?.addEventListener('click', () => {
          this.goTo(this.index + 1);
          this.restart();
        });

        // Pause autoplay during interaction.
        this.addEventListener('pointerenter', () => this.stop());
        this.addEventListener('pointerleave', () => this.restart());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.restart());

        this.onVis = () => {
          if (document.hidden) this.stop();
          else this.restart();
        };
        document.addEventListener('visibilitychange', this.onVis);
      }

      observe() {
        // Keep the active dot in sync when the user scrolls/swipes manually.
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const i = this.slides.indexOf(entry.target);
              if (i !== -1) {
                this.index = i;
                this.setActive(i);
              }
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.io.observe(slide));
      }

      goTo(i) {
        const n = this.slides.length;
        this.index = ((i % n) + n) % n;
        const slide = this.slides[this.index];
        const left = slide.offsetLeft - (this.track.clientWidth - slide.clientWidth) / 2;
        this.track.scrollTo({ left, behavior: 'smooth' });
        this.setActive(this.index);
      }

      setActive(i) {
        this.dots?.forEach((dot, di) => dot.classList.toggle('is-active', di === i));
      }

      start() {
        this.stop();
        this.timer = setInterval(() => this.goTo(this.index + 1), this.interval);
      }

      stop() {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }

      restart() {
        if (this.autoplay && !this.reduceMotion && !document.hidden) this.start();
      }
    }
  );
}
